import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Playground } from '../../components/Playground';
import { Callout } from '../../components/Callout';

export function SignalsPage(): Node {
  return html`
    <div>
      <h1>Signals</h1>
      <p>Signals are the reactive primitive in onefold. They hold a value and automatically notify subscribers when it changes.</p>

      <h2>createSignal</h2>
      ${CodeBlock(`import { createSignal } from 'onefold';

const count = createSignal(0);

count()              // read current value → 0
count.set(5)         // write a new value
count.set(n => n + 1) // update from previous → 6
count.peek()         // read without subscribing`)}

      <h2>createEffect</h2>
      <p>Run side effects whenever dependencies change. Dependencies are tracked automatically — any signal read inside the effect is subscribed to.</p>
      ${CodeBlock(`import { createEffect } from 'onefold';

createEffect(() => {
  console.log('Count changed:', count());
});
// Logs immediately, then again on every count.set() call`)}

      <p><code>createEffect</code> returns a disposer function to stop the effect:</p>
      ${CodeBlock(`const stop = createEffect(() => { /* ... */ });
stop(); // unsubscribes from all signals`)}

      <h2>createComputed</h2>
      <p>Create derived values that only recompute when their dependencies change.</p>
      ${CodeBlock(`import { createComputed } from 'onefold';

const count = createSignal(3);
const double = createComputed(() => count() * 2);

double() // 6 — cached until count changes
// double.set(10) → throws Error (read-only)`)}

      <h2>batch</h2>
      <p>Group multiple signal writes into a single effect flush to avoid intermediate renders.</p>
      ${CodeBlock(`import { batch } from 'onefold';

const a = createSignal(0);
const b = createSignal(0);

batch(() => {
  a.set(1);
  b.set(2);
}); // effects run once, not twice`)}

      ${Callout('Without batch, each set() triggers effects immediately. With batch, all sets are collected and effects fire only once at the end.')}

      <h2>Try It</h2>
      ${Playground(`import { createSignal, createComputed, html, mount } from 'onefold';

function App(): Node {
  const count = createSignal(0);
  const double = createComputed(() => count() * 2);

  return html\`
    <div>
      <p>Count: \${() => count()}</p>
      <p>Double: \${() => double()}</p>
      <button onclick=\${() => count.set(n => n + 1)}>+1</button>
      <button onclick=\${() => count.set(n => n - 1)}>-1</button>
      <button onclick=\${() => count.set(0)}>Reset</button>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Signals + Computed')}

      <h2>API Reference</h2>
      ${raw(`<table>
        <tr><th>Function</th><th>Returns</th><th>Description</th></tr>
        <tr><td><code>createSignal(initial)</code></td><td>Signal&lt;T&gt;</td><td>Create a reactive signal</td></tr>
        <tr><td><code>signal()</code></td><td>T</td><td>Read value and subscribe</td></tr>
        <tr><td><code>signal.set(value)</code></td><td>void</td><td>Set new value, notify subscribers</td></tr>
        <tr><td><code>signal.set(fn)</code></td><td>void</td><td>Update from previous value</td></tr>
        <tr><td><code>signal.peek()</code></td><td>T</td><td>Read without subscribing</td></tr>
        <tr><td><code>createEffect(fn)</code></td><td>() =&gt; void</td><td>Side effects on dependency change</td></tr>
        <tr><td><code>createComputed(fn)</code></td><td>Signal&lt;T&gt;</td><td>Cached derived computation (read-only)</td></tr>
        <tr><td><code>batch(fn)</code></td><td>void</td><td>Group updates, single flush</td></tr>
      </table>`)}
    </div>
  `;
}
